import { useState, useEffect } from 'react';
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useParams } from 'react-router-dom';
import { Button } from '../../catalyst-components/button';
import pig1 from '../../assets/graphics/pig1.gif';
import { useUnsubscribe } from '../../shared/hooks/useUnsubscribe';
import { api } from '../../convex/_generated/api';

export const Unsubscribe = () => {
  const { unsubscribeToken } = useParams<{ unsubscribeToken: string }>();
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { unsubscribe: unsubscribeUser, isLoading } = useUnsubscribe();

  // Get subscriber info to show email
  const subscriberInfo = useQuery(
    api.subscribers.getSubscriberByUnsubscribeToken,
    unsubscribeToken ? { unsubscribeToken } : "skip"
  );

  useEffect(() => {
    if (!unsubscribeToken) {
      setError('Invalid unsubscribe link');
    }
  }, [unsubscribeToken]);

  const handleUnsubscribe = async () => {
    if (!unsubscribeToken) {
      setError('Invalid unsubscribe token');
      return;
    }

    try {
      setError('');
      const result = await unsubscribeUser(unsubscribeToken);

      if (result.success) {
        setMessage(result.message);
        setIsUnsubscribed(true);
      } else {
        setError('Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      setError('Something went wrong. Please try again later.');
    }
  };

  if (!unsubscribeToken) {
    return (
      <section className="flex flex-col justify-center w-full grow items-center min-h-screen">
        <div className="flex flex-col items-center max-w-lg mx-auto px-6">
          <div className="w-72 h-72 flex items-center justify-center mb-8">
            <img
              src={pig1}
              alt="Gruntled"
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-center mb-4 text-primary-5 uppercase">
            Invalid Link
          </h1>
          <p className="text-lg text-center mb-8 text-primary-5">
            This unsubscribe link is not valid or has expired.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col justify-center w-full grow items-center min-h-screen">
      <div className="flex flex-col items-center max-w-lg mx-auto px-6">

        {/* Boar Animation */}
        <div className="w-72 h-72 flex items-center justify-center mb-8">
          <img
            src={pig1}
            alt="Gruntled"
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-center mb-4 text-primary-5 uppercase">
          {isUnsubscribed ? 'Unsubscribed' : 'Unsubscribe'}
        </h1>

        {!isUnsubscribed ? (
          <>
            {subscriberInfo && subscriberInfo.active && (
              <>
                <p className="text-lg text-center mb-2 text-primary-5">
                  Are you sure you want to unsubscribe?
                </p>

                {subscriberInfo && (
                  <p className="text-sm text-center mb-8 text-primary-4">
                    Email: {subscriberInfo.email}
                  </p>
                )}
              </>
            )}

            {subscriberInfo === null && (
              <p className="text-sm text-center mb-8 text-red-500">
                This unsubscribe link is invalid or has already been used.
              </p>
            )}

            {error && (
              <p className="text-red-500 text-center text-sm mb-4">{error}</p>
            )}

            {subscriberInfo && subscriberInfo.active && (
              <div className="w-full max-w-sm space-y-3">
                <Button
                  onClick={handleUnsubscribe}
                  color="dark"
                  className="w-full uppercase"
                  disabled={isLoading}
                >
                  {isLoading ? 'Unsubscribing...' : 'Yes, Unsubscribe Me'}
                </Button>
              </div>
            )}

            {subscriberInfo && !subscriberInfo.active && (
              <div className="text-center">
                <div className="p-6 bg-primary-2 border border-primary-4 rounded-lg mb-4">
                  <p className="text-primary-5 font-medium">
                    You are already unsubscribed.
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center">
            {message && (
              <div className="p-6 bg-primary-2 border border-primary-4 rounded-lg">
                <p className="text-primary-5 font-medium">
                  {message}
                </p>
                <p className="text-primary-4 text-sm mt-2">
                  You will no longer receive emails from us.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
